/**
 * The route sheet's validation rail.
 *
 * Flat, because a route sheet has no liable parties: FOM/1230/2013 has no
 * Cargador/Transportista split and the carrier undertaking answers for the
 * document as a whole (client answer 1, 2026-09-09). The goods rail groups by
 * party because the €401 penalty attaches per party; there is no passenger
 * analogue, so there is nothing to group by and **no penalty notice to show** —
 * printing one here would state a liability the Order does not create.
 *
 * The shape difference is real and reaches further than this file: a passenger
 * 422 carries `fieldErrors`, not `partyErrors`, so `isValidationProblem` — which
 * tests `Array.isArray(problem.partyErrors)` — answers false for one and
 * `passengerValidationProblemOf` exists to read it instead. `ValidationSummary`
 * requires `partyErrors` and can therefore never be handed one of these.
 */

import { useTranslation } from 'react-i18next'
import type { PassengerFieldError } from '@deca/api'

import { ValidationSummaryFrame, type ValidationGroup } from './ValidationSummaryFrame.tsx'

export interface PassengerValidationSummaryProps {
  errors: PassengerFieldError[] | undefined
  /** Rendered when there is nothing wrong. */
  okMessage?: string
  compact?: boolean
}

export function PassengerValidationSummary({
  errors,
  okMessage,
  compact,
}: PassengerValidationSummaryProps) {
  const { t } = useTranslation()

  const all = errors ?? []
  const groups: ValidationGroup[] = [
    {
      blocking: all.filter((error) => error.severity !== 'WARNING'),
      warnings: all.filter((error) => error.severity === 'WARNING'),
    },
  ]

  return (
    <ValidationSummaryFrame
      groups={groups}
      blockingTitle={t('errors.passengerValidationTitle')}
      seqNote={(field) => {
        // The frame hands back a goods-shaped `FieldError`; the passenger
        // contract names the line-item pointer `servicioSeq`, deliberately —
        // "a route sheet has no consignments, so `consignmentSeq` would have
        // read as a copy-paste defect for as long as this contract lives". On a
        // grouped sheet every service repeats the same labels, so a failure with
        // no service number named is a failure the user has to guess at.
        const seq = (field as PassengerFieldError).servicioSeq
        return seq == null ? null : t('passengerCreate.service', { seq })
      }}
      okMessage={okMessage}
      compact={compact}
    />
  )
}
